import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { ListChecks } from "lucide-react";
import { fmtD } from "@/lib/format";
import { CHAR_GROUPS, groupOf } from "@/lib/charGroups";

/** Popup para marcar las características de la puerta. El administrador ve valores;
 *  el usuario solo ve la descripción. El FOB nunca se muestra. */
export default function FobBuilder({ door, characteristics, onPatch, showPrices }) {
  const [open, setOpen] = useState(false);
  const [features, setFeatures] = useState(door.fob_features || []);

  useEffect(() => { setFeatures(door.fob_features || []); }, [door.fob_features]);

  const toggleFeature = (name) => {
    const next = features.includes(name) ? features.filter((n) => n !== name) : [...features, name];
    setFeatures(next);
    onPatch(door.id, { fob_mode: "auto", fob_features: next });
  };

  const count = features.length;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button data-testid={`fob-builder-btn-${door.id}`} title="Características de la puerta"
          className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${count ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-200" : "bg-slate-100 text-slate-500 hover:bg-slate-200"}`}>
          <ListChecks className="h-3.5 w-3.5" /> {count ? `${count} carac.` : "Características"}
        </button>
      </DialogTrigger>
      <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display">Características · {door.code || "puerta"}</DialogTitle>
        </DialogHeader>
        <p className="text-xs text-slate-500 -mt-2">Marca todo lo que lleva la puerta.</p>
        {showPrices && (
          <p className="text-[11px] text-amber-600 bg-amber-50 border border-amber-200 rounded px-2 py-1.5">
            Para editar precios, agregar o quitar características ve a la pestaña <b>Mantenimiento</b>. Las características nuevas quedan disponibles aquí; los cambios de precio de las existentes solo aplican a cotizaciones nuevas.
          </p>
        )}
        <div className="space-y-3 mt-2">
          {CHAR_GROUPS.map((g) => {
            const items = (characteristics || []).filter((c) => groupOf(c) === g.key);
            if (items.length === 0) return null;
            return (
              <div key={g.key} data-testid={`char-group-${g.key}`}>
                <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 px-1 mb-0.5 border-b border-slate-100 pb-1">
                  {g.label}
                </p>
                <div className="space-y-0.5">
                  {items.map((c) => (
                    <label key={c.name} data-testid={`char-${door.id}-${c.name}`}
                      className="flex items-center gap-2.5 py-1.5 px-1 rounded hover:bg-slate-50 cursor-pointer">
                      <Checkbox checked={features.includes(c.name)} onCheckedChange={() => toggleFeature(c.name)} />
                      <span className="text-sm text-slate-700 flex-1">{c.name}</span>
                      {showPrices && c.price !== undefined && (
                        <span className="font-mono text-xs text-slate-500">{fmtD(c.price)}</span>
                      )}
                    </label>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
}
